import { ChevronIcon } from 'assets';
import React, { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { RadioInput } from 'components/form/radio';
import { useForm } from 'react-hook-form';
import { useMobile } from 'hooks';
import { useReportDetails } from 'hooks/useReportDetails';
import { useFetchDecisionBuckets } from 'hooks/useFetchDecisionBuckets';
import { useUpdateCandidateDecision, UpdateDecisionType } from 'hooks/useUpdateCandidateDecision';
import styles from './styles.module.scss';

interface ComparisonSection {
  title: string;
  candidates: Array<{
    id: string;
    name: string;
    score: number;
    color: string;
  }>;
}

interface MultiCandidateComparisonProps {
  sections: ComparisonSection[];
  assessmentId?: string;
  primaryCandidateId?: string;
  onViewReport?: (candidateId: string) => void;
  onRemoveCandidate?: (candidateId: string) => void;
}

interface CandidateDecisionContentProps {
  candidate: {
    id: string;
    name: string;
    score: number;
    color: string;
  };
  assessmentId?: string;
  primaryCandidateId?: string;
  onViewReport?: (candidateId: string) => void;
  onRemoveCandidate?: (candidateId: string) => void;
}

const CandidateDecisionContent: React.FC<CandidateDecisionContentProps> = ({
  candidate,
  assessmentId,
  primaryCandidateId,
  onViewReport,
  onRemoveCandidate,
}) => {
  const { reportDetails } = useReportDetails(candidate.id);
  const { data: decisionBucketsData } = useFetchDecisionBuckets(assessmentId);
  const { updateDecision } = useUpdateCandidateDecision();

  const decisionOptions = (decisionBucketsData?.decision_buckets || []).map(b => ({
    label: b.decision,
    value: b.decision,
  }));

  const { register, setValue, watch } = useForm({ defaultValues: { decision: '' } });
  const decisionValue = watch('decision');

  useEffect(() => {
    if (reportDetails?.response_details?.bucket) {
      setValue('decision', reportDetails.response_details.bucket);
    }
  }, [reportDetails?.response_details?.bucket]);

  const [isDecisionOpen, setIsDecisionOpen] = useState(true);

  const handleDecisionChange = (value: string) => {
    if (!reportDetails?.response_details?.id) return;
    const decisionMap: Record<string, string> = {
      'Interview': 'interview',
      'Revisit': 'revisit',
      'Probe further': 'probe_further',
      'Request More Info': 'request_more_info',
      'Save for future': 'save_for_future',
      'Reject': 'reject',
    };
    const apiDecision = decisionMap[value] || value.toLowerCase().replace(/\s+/g, '_');
    setValue('decision', value);
    updateDecision({
      assessment_response_ids: [reportDetails.response_details.id],
      decision: apiDecision as UpdateDecisionType,
    });
  };

  return (
    <div className="w-[187px]">
      {/* Decisions header */}
      <div className="px-4 py-3">
        <div
          className="flex justify-between items-center bg-[#EFF8FF] rounded-md px-3 py-2 cursor-pointer"
          onClick={() => setIsDecisionOpen(!isDecisionOpen)}
        >
          <h3 className="text-base text-[#344054]">Decisions</h3>
          <ChevronIcon className={`w-4 h-4 transition-transform ${isDecisionOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-4 pt-0">
        {isDecisionOpen && (
          <div className="space-y-3 pb-4 w-[90%] mx-auto">
            <RadioInput
              name="decision"
              type="radio"
              register={register}
              value={decisionValue}
              options={decisionOptions.length > 0 ? decisionOptions : [
                { label: "Probe further", value: "probe_further" },
                { label: "Save for future", value: "save_for_future" },
                { label: "Interview", value: "interview" },
                { label: "Reject", value: "reject" },
              ]}
              validatorMessage=""
              parentClassName={`
                [&>label]:w-full
                [&>label]:mb-2
                [&>label]:text-[#344054]
                [&>label]:text-sm
                [&>label]:cursor-pointer
                [&>label>div]:flex
                [&>label>div]:items-center
                [&>label>div>input]:mr-2
                [&>label>div>input]:w-4
                [&>label>div>input]:h-4
                !mb-0
                ${styles.radioButton}
              `}
              onChange={(e) => handleDecisionChange(e.target.value)}
            />
          </div>
        )}

        {/* View Report */}
        <Popover.Close asChild>
          <button
            onClick={() => onViewReport?.(candidate.id)}
            className="w-full py-0 h-fit mb-4 text-[#667085] text-sm !bg-transparent hover:text-[#1D2939] transition-colors text-left"
          >
            View report
          </button>
        </Popover.Close>

        {/* Remove Candidate */}
        {candidate.id !== primaryCandidateId ? (
          <Popover.Close asChild>
            <button
              onClick={() => onRemoveCandidate?.(candidate.id)}
              className="w-full py-0 h-fit text-[#E53E3E] text-sm !bg-transparent hover:text-[#C53030] transition-colors text-left"
            >
              Remove candidate
            </button>
          </Popover.Close>
        ) : (
          <p className="w-full py-0 h-fit text-[#98A2B3] text-sm text-left cursor-default">
            Can't remove primary candidate
          </p>
        )}
      </div>
    </div>
  );
};

const MultiCandidateComparison: React.FC<MultiCandidateComparisonProps> = ({
  sections,
  assessmentId,
  primaryCandidateId,
  onViewReport,
  onRemoveCandidate,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['Basic Expectation']));
  const { isMobile } = useMobile({ size: 640 });

  const toggleSection = (sectionTitle: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionTitle)) {
      newExpanded.delete(sectionTitle);
    } else {
      newExpanded.add(sectionTitle);
    }
    setExpandedSections(newExpanded);
  };

  const getDisplayName = (fullName: string) => {
    if (isMobile) return fullName.split(' ')[0];
    return fullName;
  };

  return (
    <div className="relative sm:mx-12 mx-6">
      <div className="space-y-4">
        {sections.map((section, index) => {
          const isExpanded = expandedSections.has(section.title);

          return (
            <div key={index} className="bg-white border border-[#EAECF0] rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full !bg-white flex justify-between items-center p-4 hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-[#1D2939] font-medium text-lg">{section.title}</h3>
                <ChevronIcon
                  className={`w-5 h-5 text-[#667085] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              <hr className="border-[#EAECF0]" />
              {isExpanded && (
                <div className="sm:px-4 px-2.5 pb-4 pt-8 xl:w-[70%] w-full xl:ml-[10%]">
                  {section.candidates.map((candidate, candidateIndex) => (
                    <div key={candidateIndex} className="sm:flex grid grid-cols-[1fr_2fr] items-center">
                      <p className="text-right sm:w-[20%] w-auto sm:flex-none flex-1 last:border-b-none text-[#1D2939] font-medium">
                        <span className="pr-3">{getDisplayName(candidate.name)}</span>
                        {candidateIndex < section.candidates.length - 1 && (
                          <hr className="border-[#EAECF0] w-[50%] ml-auto my-[7px]" />
                        )}
                      </p>

                      <div className="flex flex-1 items-center gap-3 border-l border-[#EAECF0] w-full">
                        <div className="flex-1 bg-[#F2F4F7] rounded-r-full sm:h-2 h-1.5 relative overflow-hidden">
                          <div
                            className="h-full rounded-r-full transition-all duration-300"
                            style={{ width: `${candidate.score}%`, backgroundColor: candidate.color }}
                          />
                        </div>

                        <Popover.Root>
                          <Popover.Trigger asChild>
                            <div className="flex items-center gap-1 cursor-pointer">
                              <span className="text-[#667085] sm:text-sm text-xs font-medium min-w-0">
                                {candidate.score}%
                              </span>
                              <button className="text-[#667085] hover:text-[#1D2939] !bg-transparent transition-colors !ml-0">
                                <ChevronIcon className="sm:ml-0 -ml-4 w-4 h-4" />
                              </button>
                            </div>
                          </Popover.Trigger>

                          <Popover.Portal>
                            <Popover.Content
                              side="bottom"
                              align="center"
                              sideOffset={8}
                              collisionPadding={8}
                              className="bg-white rounded-xl shadow-lg border border-[#E2E8F0] z-[9999] outline-none"
                            >
                              <CandidateDecisionContent
                                candidate={candidate}
                                assessmentId={assessmentId}
                                primaryCandidateId={primaryCandidateId}
                                onViewReport={onViewReport}
                                onRemoveCandidate={onRemoveCandidate}
                              />
                              <Popover.Arrow className="fill-white" />
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { MultiCandidateComparison };
